import React from 'react';
import { MockService, SV } from 'types/rest/mockServiceTypes';
import Tab from 'react-bootstrap/Tab';
import MockServiceTransactionShuttle from '../MockServiceTransactionShuttle';
import { isOplLocation } from 'utils/mockServiceUtils';
import { MockParameterState } from '../MockParameters';
import RuntimeConfigTab from '../RuntimeConfigTab';
import Tabs from 'react-bootstrap/Tabs';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import MockServiceDataSettings from 'views/MockServices/Tabs/MockServices/DataSettings/MockServiceDataSettings';
import MockServiceRunners from './Runners/MockServiceRunners';
import MockParameterHandlerWrapper from '../Params/MockParameterHandlerWrapper';
import RunnerSslConfig from './Runners/RunnerSslConfig';
import { isTemplate } from 'utils/miscUtil';
import TemplatesTransactionShuttle from '../../MockTemplates/TemplatesTransactionShuttle';
import AdvancedLogging from './AdvancedLogging/AdvancedLogging';
import MemorySettings from '../Params/MemorySettings';

interface MockServiceDetailTabProps {
  mockService: MockService;
  changeActiveTabHandler: (e: any) => void;
  activeTab: any;
  disabled: boolean;
  updateParameters: (state: MockParameterState, debounce?: boolean) => void;
  updateRunTimeConfig?: (config: any) => void;
  updateIterationOverrides: (overrides: any) => void;
}

/**
 * Renders tabs based on Mock Service Type.
 * @constructor
 */
const MockServiceDetailTabs = ({
  activeTab,
  changeActiveTabHandler,
  mockService,
  disabled,
  updateRunTimeConfig,
  updateIterationOverrides,
  updateParameters,
}: MockServiceDetailTabProps) => {
  const advancedLogging = (
    <Tab key={'fileLogging'} eventKey="fileLogging" title={'Logger Settings'}>
      <AdvancedLogging
        disabled={disabled}
        loggingConfig={mockService.loggingConfig}
        onUpdate={(loggingConfig, debounce) => {
          const update = { ...mockService, loggingConfig: loggingConfig };
          updateParameters(update, debounce);
        }}
      />
    </Tab>
  );

  const dataSettingsTab = (
    <Tab key={'dataSettings'} eventKey="dataSettings" title={'Data Settings'} mountOnEnter={true} unmountOnExit={true}>
      <MockServiceDataSettings
        mockServiceEntity={mockService}
        key={'mock-service-' + mockService.id}
        onUpdateIterationOverrides={updateIterationOverrides}
        disabled={disabled}
        onTabChange={changeActiveTabHandler}
        isMsStateful={mockService.metadata?.badges?.STATE_UPDATE ? mockService.metadata.badges.STATE_UPDATE : false}
        isMsRunning={mockService.status === 'RUNNING'}
      />
    </Tab>
  );

  const runnersConfigTab = (
    <Tab
      key={'runTimeSettings'}
      eventKey="runtimeSettings"
      title={'Runtime Properties'}
      mountOnEnter={false}
      unmountOnExit={false}
    >
      <div className={'mt-5'}>
        <RuntimeConfigTab currenTab={activeTab} state={{ ...mockService }} updateState={updateRunTimeConfig} />
      </div>
    </Tab>
  );

  /**
   * Runner tabs are shown when the Messaging flag is switched on.
   */
  const getRunnerTabs = () => {
    return [
      <Tab key={'main'} eventKey="main" title={'Runner'}>
        <MockServiceRunners mockService={mockService} onUpdate={updateParameters} disabled={disabled} />
      </Tab>,
      dataSettingsTab,
      !isTemplate(mockService) && runnersConfigTab,
      (isOplLocation(mockService.harborId, mockService.shipId) || isTemplate(mockService)) && (
        <Tab key={'ssl'} eventKey="ssl" title={'SSL'}>
          <RunnerSslConfig mockService={mockService} disabled={disabled} />
        </Tab>
      ),
      isOplLocation(mockService.harborId, mockService.shipId) && (
        <Tab key={'memory'} eventKey="memory" title={'Memory'}>
          <MemorySettings mockService={mockService} onUpdateState={updateParameters} disabled={disabled} />
        </Tab>
      ),
      //TODO - hide behind feature flag
      advancedLogging,
    ];
  };

  const getTabs = () => {
    return getRunnerTabs();
  };

  return (
    <>
      <Tabs
        transition={false}
        id="service-mock-tabs"
        activeKey={activeTab}
        onSelect={changeActiveTabHandler}
        mountOnEnter={true}
      >
        {getTabs()}
      </Tabs>
    </>
  );
};

export default MockServiceDetailTabs;
